import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Info } from "lucide-react";

export const TERMS_VERSION = "1.0";

export default function SubscriptionTermsDialog({ open, onOpenChange, planName, planPrice, busy, onConfirm, trialDaysLeft = 0 }) {
  const [accepted, setAccepted] = useState(false);
  useEffect(() => { if (!open) setAccepted(false); }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="terms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" /> Contrato de Assinatura e Termos de Uso
          </DialogTitle>
          <DialogDescription>
            {planName ? `Plano ${planName}${planPrice != null ? ` — R$ ${Number(planPrice).toFixed(0)}/mês` : ""}. ` : ""}
            Leia e aceite as condições abaixo para prosseguir com a assinatura. Versão {TERMS_VERSION}.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[52vh] rounded-md border border-border p-4" data-testid="terms-content">
          <div className="space-y-4 text-sm leading-relaxed text-muted-foreground pr-3">
            <p className="text-foreground font-medium">
              Este Contrato de Assinatura de Software como Serviço ("Contrato") regula o uso da plataforma
              Sapientia OS ("Software"), um sistema de gestão financeira e de margem por lotes, disponibilizado
              na modalidade SaaS. Ao marcar a caixa de aceite e concluir a assinatura, o Contratante declara
              ter lido, compreendido e concordado integralmente com as cláusulas a seguir.
            </p>

            <div>
              <h4 className="font-semibold text-foreground">1. Objeto</h4>
              <p>1.1. O presente Contrato concede ao Contratante uma licença de uso não exclusiva, intransferível
                e revogável do Software, para fins de gestão operacional e financeira do seu negócio, nos limites
                do plano contratado.</p>
              <p>1.2. O Software é fornecido em regime de assinatura recorrente, acessível via internet, sem
                transferência de código-fonte ou propriedade.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">2. Planos, Preços e Pagamento</h4>
              <p>2.1. Os valores, limites e recursos de cada plano são os exibidos na página de Plano e Assinatura
                no momento da contratação, cobrados em Reais (BRL).</p>
              <p>2.2. O processamento de pagamentos é realizado por meio da Stripe, Inc. O Contratante autoriza a
                cobrança recorrente mensal no meio de pagamento informado, até o cancelamento.</p>
              <p>2.3. O não pagamento poderá acarretar a suspensão do acesso às funções de criação de dados,
                preservando-se o acesso de leitura às informações já existentes, conforme política da plataforma.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">3. Período de Teste e Renovação</h4>
              <p>3.1. Quando aplicável, o período de teste gratuito tem a duração indicada na contratação. Ao término,
                a assinatura é convertida em cobrança recorrente, salvo cancelamento prévio.</p>
              <p>3.2. A assinatura é renovada automaticamente a cada ciclo mensal, pelo preço vigente, até que o
                Contratante solicite o cancelamento.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">4. Cancelamento e Reembolso</h4>
              <p>4.1. O Contratante pode cancelar a assinatura a qualquer momento; o cancelamento produz efeitos ao
                final do ciclo de cobrança vigente, mantendo-se o acesso até lá.</p>
              <p>4.2. Salvo disposição legal em contrário, os valores de ciclos já faturados não são reembolsáveis
                proporcionalmente. Direitos do consumidor previstos no CDC são integralmente respeitados.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">5. Obrigações do Contratante</h4>
              <p>5.1. Utilizar o Software de forma lícita, fornecendo dados verdadeiros e mantendo a confidencialidade
                das credenciais de acesso.</p>
              <p>5.2. Responsabilizar-se pelos lançamentos, cálculos de margem, custos e informações financeiras que
                inserir, bem como pelas decisões tomadas com base nos relatórios gerados.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">6. Proteção de Dados (LGPD)</h4>
              <p>6.1. O tratamento de dados pessoais observa a Lei nº 13.709/2018 (LGPD). Os dados são utilizados
                exclusivamente para a prestação do serviço, com medidas de segurança técnicas e organizacionais.</p>
              <p>6.2. Cada organização (tenant) possui isolamento lógico de dados. O Contratante pode solicitar
                exportação e eliminação dos seus dados conforme a legislação aplicável.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">7. Propriedade Intelectual</h4>
              <p>7.1. Todos os direitos sobre o Software, marca, layout e código pertencem ao fornecedor. A assinatura
                não transfere qualquer direito de propriedade intelectual.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">8. Disponibilidade e Limitação de Responsabilidade</h4>
              <p>8.1. O fornecedor empreenderá esforços razoáveis para manter a disponibilidade do serviço, sem garantir
                operação ininterrupta ou isenta de erros.</p>
              <p>8.2. O Software é uma ferramenta de apoio à gestão e não constitui aconselhamento contábil, fiscal,
                jurídico ou de investimento. O fornecedor não se responsabiliza por lucros cessantes ou danos indiretos
                decorrentes do uso, limitando-se eventual responsabilidade ao valor das mensalidades dos últimos 12 meses.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">9. Vigência e Rescisão</h4>
              <p>9.1. Este Contrato vigora enquanto a assinatura estiver ativa. O descumprimento de cláusulas poderá
                ensejar a suspensão ou rescisão, resguardado o acesso para exportação dos dados por prazo razoável.</p>
            </div>

            <div>
              <h4 className="font-semibold text-foreground">10. Disposições Gerais e Foro</h4>
              <p>10.1. Alterações destes termos serão comunicadas e passarão a vigorar a partir do próximo ciclo de
                cobrança. O uso continuado implica concordância com a versão vigente.</p>
              <p>10.2. Fica eleito o foro da comarca do domicílio do Contratante para dirimir controvérsias, com
                renúncia a qualquer outro, por mais privilegiado que seja.</p>
            </div>

            <p className="text-xs">Documento meramente ilustrativo (modelo para software financeiro). Recomenda-se revisão
              jurídica antes do uso comercial definitivo.</p>
          </div>
        </ScrollArea>

        {trialDaysLeft > 0 ? (
          <div className="flex gap-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm dark:border-sky-900 dark:bg-sky-950" data-testid="trial-notice">
            <Info className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
            <p className="text-sky-900 dark:text-sky-100">
              Você está em <strong>período de teste</strong> ({trialDaysLeft} {trialDaysLeft === 1 ? "dia restante" : "dias restantes"}).
              Neste momento <strong>apenas validamos o seu cartão</strong> — <strong>nenhum valor é cobrado hoje</strong>.
              A cobrança{planPrice != null ? ` de R$ ${Number(planPrice).toFixed(0)}/mês` : ""} ocorrerá automaticamente ao fim do período de testes, e você pode cancelar antes disso.
            </p>
          </div>
        ) : (
          <div className="flex gap-3 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm dark:border-sky-900 dark:bg-sky-950" data-testid="charge-notice">
            <Info className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
            <p className="text-sky-900 dark:text-sky-100">
              Ao continuar, sua assinatura{planPrice != null ? ` de R$ ${Number(planPrice).toFixed(0)}/mês` : ""} será cobrada conforme o plano selecionado, com renovação automática mensal.
            </p>
          </div>
        )}

        <label htmlFor="accept-terms" className="flex items-start gap-3 rounded-md border border-border bg-secondary/40 p-3 cursor-pointer">
          <Checkbox id="accept-terms" checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} className="mt-0.5" data-testid="accept-terms-checkbox" />
          <span className="text-sm text-foreground">Li e aceito o Contrato de Assinatura, os Termos de Uso e a Política de Privacidade do Sapientia OS.</span>
        </label>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="terms-cancel-button">Cancelar</Button>
          <Button disabled={!accepted || busy} onClick={onConfirm} data-testid="terms-confirm-button">
            {busy ? "Processando..." : "Aceitar e continuar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
